import {
  getReplaceableClosingGroup,
  shouldApplyGroupSymbols,
  shouldCloseOpenGroupLeft,
  shouldCloseOpenGroupRight,
} from "@/input/utils/openGroup";
import {
  closingToOpeningMap,
  isGroupClosingSymbol,
  isGroupOpeningSymbol,
  openingToClosingMap,
} from "@/models/nodes/groups/utils";

import { groupsJson } from "./data";
import { filterSuggestions } from "./utils/filterSuggestions";
import { normalizeSuggestionKeywords } from "./utils/normalizeKeywords";

import type { GroupJson } from "./data/types";
import type { SuggestionProvider, SingleSuggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import type { NodeModel } from "@/models/nodes/types";

type GroupOptions = {
  closeGroupOnLeft: GroupNodeModel | null;
  closeGroupOnRight: GroupNodeModel | null;
  nodeBeforeCursor: NodeModel | undefined;
  nodeAfterCursor: NodeModel | undefined;
};

const ONLY_SYMBOLS_REGEX = "^[^\\p{Letter}\\d]+.?$";

function getOpenGroupSuggestions(group: GroupJson, options: GroupOptions) {
  const { closeGroupOnLeft } = options;

  return {
    type: "single",
    item: {
      category: group.category,
      props: {
        openingSymbol: group.openingSymbol,

        // if there is a group to close on its left side, show its closing bracket in suggestion
        closingSymbol: closeGroupOnLeft
          ? {
              symbol: closeGroupOnLeft.closingSymbol,
            }
          : undefined,
      },
      type: "group",
      intlName: group.intlName,
      symbol: group.openingSymbol.symbol,
      keywords: normalizeSuggestionKeywords(group, { keywords: group.openingSymbol.keywords }),
      placeholders: {
        children: ["filled"],
      },
    },
    canBeAutocompleted:
      "canBeAutocompleted" in group
        ? group.canBeAutocompleted
        : {
            math: {
              regex: ONLY_SYMBOLS_REGEX,
            },
          },
    action: {
      type: "open-group",
    },
    priority: group.priority,
    substitution: {
      cursorPlacement: "child:0:start",
    },
  } as SingleSuggestion<"group">;
}

function getCloseGroupSuggestions(group: GroupJson, options: GroupOptions) {
  const { closeGroupOnRight, nodeBeforeCursor } = options;

  if (!closeGroupOnRight && group.closingSymbol.symbol === group.openingSymbol.symbol) return null;

  return {
    type: "single",
    item: {
      category: group.category,
      props: {
        // if there is a group to close on its right side, show its opening bracket in suggestion
        openingSymbol: closeGroupOnRight
          ? {
              symbol: closeGroupOnRight.openingSymbol,
            }
          : undefined,
        closingSymbol: group.closingSymbol,
      },
      type: "group",
      intlName: group.intlName,
      symbol: group.closingSymbol.symbol,
      keywords: normalizeSuggestionKeywords(group, { keywords: group.closingSymbol.keywords }),
      placeholders: {
        children: ["filled"],
      },
    },
    canBeAutocompleted:
      "canBeAutocompleted" in group
        ? group.canBeAutocompleted
        : {
            math: {
              regex: ONLY_SYMBOLS_REGEX,
            },
          },
    action: {
      type: "close-group",
    },
    priority: group.priority,

    substitution: {
      cursorPlacement: "after",
      nodesBefore: nodeBeforeCursor
        ? {
            match: { maxCount: 1 },
            moveTo: 0,
          }
        : undefined,
    },
  } as SingleSuggestion<"group">;
}

const groupSuggestions = Object.values(groupsJson).map((group) => {
  return {
    type: "single",
    item: {
      category: group.category,
      props: {
        openingSymbol: group.openingSymbol,
        closingSymbol: group.closingSymbol,
      },
      type: "group",
      intlName: group.intlName,
      symbol: group.openingSymbol.symbol,
      keywords: normalizeSuggestionKeywords(group, { keywords: group.openingSymbol.keywords }),
    },
    canBeAutocompleted:
      "canBeAutocompleted" in group
        ? group.canBeAutocompleted
        : {
            math: {
              regex: ONLY_SYMBOLS_REGEX,
            },
          },
    priority: group.priority,
    substitution: {
      cursorPlacement: "child:0:start",
    },
  } as SingleSuggestion<"group">;
});

const applyGroup = (doc: DocumentModel): SingleSuggestion<"group"> => {
  const { direction } = doc.selection;

  return {
    type: "single",
    item: {
      type: "group",
      intlName: "group.brackets",
      symbol: "()",
      keywords: normalizeSuggestionKeywords({
        keywords: ["group", "brackets", "parentheses"],
      }),
      props: {
        openingSymbol: groupsJson.round.openingSymbol,
        closingSymbol: groupsJson.round.closingSymbol,
      },
    },
    popupGroup: "apply",
    priority: 1.5,
    substitution: {
      cursorPlacement: direction === "backward" ? "child:0:start" : "child:0:end",
      selectedNodes: { moveTo: 0 },
    },
  };
};

export const groupSuggestionsProvider: SuggestionProvider = {
  type: "group",
  search: (str, doc, lang) => {
    if (doc.selection.hasSelectedNodes) {
      const canGroup = doc.selection.isMathOnlySelection && doc.selection.selectedNodes.length > 1;

      return canGroup ? [[applyGroup(doc)]] : [[]];
    }

    if (shouldApplyGroupSymbols(doc)) {
      const setGroupSuggestions = groupSuggestions.map((sugg) => ({
        ...sugg,
        action: {
          type: "set-function-group-symbols" as const,
        },
      }));

      return [filterSuggestions(str, setGroupSuggestions)];
    }

    // typing a non-matching closing symbol at the end of an already closed group should offer
    // to re-close it with the new symbol, same as for an open group
    const closeGroupOnRight =
      shouldCloseOpenGroupRight(doc) ?? getReplaceableClosingGroup(doc, str);
    const closeGroupOnLeft = shouldCloseOpenGroupLeft(doc);

    const suggestions: SingleSuggestion<"group">[] = [];

    const { nodeBeforeCursor, nodeAfterCursor } = doc.cursor;

    const options = {
      closeGroupOnLeft,
      closeGroupOnRight,
      nodeBeforeCursor,
      nodeAfterCursor,
    } as const;

    if (
      closeGroupOnRight &&
      isGroupClosingSymbol(str) &&
      closeGroupOnRight.openingSymbol === closingToOpeningMap[str]
    ) {
      const group = Object.values(groupsJson).find((g) => g.closingSymbol.symbol === str);

      if (group) {
        return [[getCloseGroupSuggestions(group, options)].filter(Boolean)];
      }
    }

    if (
      closeGroupOnLeft &&
      isGroupOpeningSymbol(str) &&
      closeGroupOnLeft.closingSymbol === openingToClosingMap[str]
    ) {
      const group = Object.values(groupsJson).find((g) => g.openingSymbol.symbol === str);

      if (group) {
        return [[getOpenGroupSuggestions(group, options)].filter(Boolean)];
      }
    }

    const openGroupSuggestions = Object.values(groupsJson).map((group) => {
      return getOpenGroupSuggestions(group, options);
    });

    const closeGroupSuggestions = Object.values(groupsJson)
      .map((group) => {
        return getCloseGroupSuggestions(group, options);
      })
      .filter(Boolean);

    // this filtering could be done via filterRule (if it would take into account "shouldClose" states)
    if (!closeGroupOnLeft && !closeGroupOnRight) {
      if (nodeAfterCursor) {
        suggestions.push(...openGroupSuggestions);
      } else if (doc.editor.editorState.isExpression) {
        suggestions.push(...groupSuggestions);
      } else {
        suggestions.push(
          ...groupSuggestions.map((suggestion) => {
            const { openingSymbol, closingSymbol } = suggestion.item.props!;

            return {
              ...suggestion,
              item: {
                ...suggestion.item,
                symbol: `${openingSymbol!.symbol}${closingSymbol!.symbol}`,
              },
            };
          }),
        );
      }

      if (doc.editor.editorState.isExpression) {
        suggestions.push(...closeGroupSuggestions);
      }
    } else if (closeGroupOnRight) {
      if (nodeAfterCursor) {
        suggestions.push(...closeGroupSuggestions, ...openGroupSuggestions);
      } else {
        suggestions.push(...closeGroupSuggestions, ...groupSuggestions);
      }
    } else {
      suggestions.push(...openGroupSuggestions, ...closeGroupSuggestions);
    }

    // similarly, this could be done via priorityRule
    const result = filterSuggestions(str, suggestions, { lang }).map((sugg) => {
      if (sugg.item.type !== "group") return sugg;

      let { priority } = sugg;

      if (!closeGroupOnLeft && !closeGroupOnRight) {
        if (nodeAfterCursor && !sugg.action) {
          priority -= 0.5;
        }
        if (!nodeAfterCursor && !sugg.action) {
          priority += 0.5;
        }
      }

      if (closeGroupOnRight && sugg.action?.type === "open-group") {
        priority -= 0.5;
      }

      if (closeGroupOnLeft && sugg.action?.type === "close-group") {
        priority -= 0.5;
      }

      if (
        closeGroupOnRight &&
        sugg.action?.type === "close-group" &&
        sugg.item.props?.openingSymbol?.symbol === closeGroupOnRight?.openingSymbol
      ) {
        priority += 0.5;
      }

      if (
        closeGroupOnLeft &&
        sugg.action?.type === "open-group" &&
        sugg.item.props?.closingSymbol?.symbol === closeGroupOnLeft.closingSymbol
      ) {
        priority += 0.5;
      }

      return { ...sugg, priority };
    });

    return [result];
  },
};
